import onetime from "@-/com.npmjs.onetime";
import freeTierGraphemes from "#free-tier-graphemes" with { type: "json" };

/**
 * The curriculum grade the FREE tier runs through: HSK 1 for simplified, TBCL 1 for traditional.
 *
 * The boundary is a curriculum milestone rather than a level count, because the level generator's
 * level size is a tuning knob: pinning a count meant every resize silently moved the paywall (50
 * levels was ~HSK 1 at 8 cards per level, and ~HSK 3 once levels doubled). A level is free iff its
 * `curriculumGrade` is at most this grade — i.e. every level up to and including the one that
 * introduces the script's last grade-1 target character. `curriculumGrade` already folds a
 * higher-grade prerequisite into the earlier grade it helps teach (see
 * `projectRuntimeCharacterLevels`), so the boundary always falls on a whole level.
 *
 * Three things are deliberately pinned to it so they can never disagree:
 *
 * 1. Which graphemes a free user may ADD to study (this package).
 * 2. Which mnemonic descriptions ship unpaywalled (`getNonPremiumDescriptionGraphemes`).
 * 3. What the bundled offline shards contain (`getLiteSeedGlyphs`).
 *
 * (3) is why they must match: the offline artifacts hold exactly the free set, so a free user is
 * never offered a card whose reference data was not bundled, and a paying user who wants more
 * offline downloads the full `static.db`.
 */
export const FREE_TIER_CURRICULUM_GRADE = 1;

/** Whether a runtime curriculum level is inside the free tier. */
export function isFreeTierLevel(level: { curriculumGrade: number }): boolean {
  return level.curriculumGrade <= FREE_TIER_CURRICULUM_GRADE;
}

/**
 * The free tier's levels: the leading levels up to the last grade-1 level. Grades never decrease
 * along the curriculum, so this is always a prefix.
 */
export function getFreeTierLevels<T extends { curriculumGrade: number }>(
  levels: readonly T[],
): readonly T[] {
  return levels.filter((level) => isFreeTierLevel(level));
}

/** Return the full catalog for premium viewers and only the free leading levels for everyone else. */
export function getAccessibleLevels<T extends { curriculumGrade: number }>(
  levels: readonly T[],
  isPremium: boolean,
): readonly T[] {
  return isPremium ? levels : getFreeTierLevels(levels);
}

/**
 * Every grapheme a free user may add to study — the union across BOTH scripts.
 *
 * Union rather than per-script because a device can switch script at any time and the two curricula
 * introduce different graphemes at different points; gating per-script would make a card addable,
 * then retroactively un-addable, on a script switch. This matches the bundled shards, which are
 * seeded from the same union.
 */
export const getFreeTierGraphemes = onetime((): ReadonlySet<string> => new Set(freeTierGraphemes));

/**
 * Whether `grapheme` is inside the free tier.
 *
 * UI gating only. This decides what the app OFFERS, not what it protects: premium mnemonic text is
 * stripped from the client artifacts and served only by the authenticated worker route, which
 * re-checks entitlement server-side. Nothing here is a security boundary.
 */
export function isFreeTierGrapheme(grapheme: string): boolean {
  return getFreeTierGraphemes().has(grapheme);
}
